import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import { formatSummaryDateWithYear } from '@/utils/date';
import { useMemo } from 'react';

export interface LastPurchaseInfo {
  display: string;
  fulfillmentDate: string | null;
  orderDate: string | null;
  quantity: number;
}

export type LastPurchaseInfoMap = Record<string, LastPurchaseInfo | undefined>;

/**
 * Minimal shape needed to format a fulfillment-date display string. Decouples
 * the formatter from `LastPurchaseInfo` so the transformer can format raw
 * query results before they're wrapped into the full `LastPurchaseInfo`
 * (which includes the formatted `display` string itself).
 */
export interface FulfillmentDateInput {
  fulfillmentDate: string | null;
  orderDate: string | null;
  quantity: number;
}

/**
 * Raw query item shape from `getVariantPackLastPurchaseInfo`. Defined here
 * (rather than imported from the generated query types) so the transformer
 * is decoupled from any specific query and can accept any data source whose
 * fields match.
 */
export interface FulfilledDateTransformerItem {
  fulfillment_date?: string | null;
  order_date?: string | null;
  quantity?: number | null;
  variant_pack_id?: string | null;
}

/**
 * Comparator for two {@link LastPurchaseInfo} values used by the "Fulfilled"
 * column's tanstack `sortingFn`. Compares the most relevant date (fulfillment
 * date, falling back to order date) via lexicographic compare on raw backend
 * date strings — works for ISO-8601 because all rows use the same format.
 *
 * Rows missing both dates are handled defensively here, but the column should
 * also set `sortUndefined: 'last'` so tanstack short-circuits them before
 * this comparator runs (direction-independent placement).
 *
 * @param infoA - The last purchase info for the first row.
 * @param infoB - The last purchase info for the second row.
 * @returns A negative number if A sorts before B, positive if after, 0 if equal.
 */
export const compareFulfillmentDates = (
  infoA: LastPurchaseInfo | undefined,
  infoB: LastPurchaseInfo | undefined,
): number => {
  const dateA = infoA?.fulfillmentDate ?? infoA?.orderDate;
  const dateB = infoB?.fulfillmentDate ?? infoB?.orderDate;

  if (!dateA && !dateB) {
    return 0;
  }
  if (!dateA) {
    return 1;
  }
  if (!dateB) {
    return -1;
  }
  if (dateA === dateB) {
    return 0;
  }
  return dateA > dateB ? 1 : -1;
};

/**
 * Formats last-purchase info into a compact display string for the
 * "Fulfilled" column in the order entry table.
 *
 * If the fulfillment date is in the past the label reads "fulfilled";
 * otherwise "arriving". Quantity is always included (e.g. "3 fulfilled,
 * Apr 3"). The date is rendered in the restaurant's timezone so all rows
 * agree on what "today" means regardless of where the sales rep is.
 *
 * @param info - The last purchase info for a variant pack, or undefined if none exists.
 * @param timezone - IANA timezone identifier used to interpret and render the date.
 * @returns A formatted display string, or an empty string when there is no data.
 *
 * @example
 * formatFulfillmentDateDisplay(
 *   { fulfillmentDate: '2026-04-03', orderDate: null, quantity: 3 },
 *   'America/New_York',
 * )
 * // => "3 fulfilled, Apr 3"
 *
 * @example
 * formatFulfillmentDateDisplay(undefined, 'America/New_York')
 * // => ""
 */
export const formatFulfillmentDateDisplay = (
  info: FulfillmentDateInput | undefined,
  timezone: string,
): string => {
  if (!info) {
    return '';
  }

  const metricDate = info.fulfillmentDate ?? info.orderDate;
  if (!metricDate) {
    return '';
  }

  const dateMoment = moment.tz(metricDate, timezone);
  if (!dateMoment.isValid()) {
    return '';
  }

  const nowMoment = moment.tz(timezone);
  const formattedDate = formatSummaryDateWithYear(dateMoment);

  const isFulfilled = dateMoment.isBefore(nowMoment.startOf('day'));

  return isFulfilled
    ? i18n.t('sales_portal.order_entry.table.fulfilled_quantity_date', {
        date: formattedDate,
        quantity: info.quantity,
      })
    : i18n.t('sales_portal.order_entry.table.arriving_quantity_date', {
        date: formattedDate,
        quantity: info.quantity,
      });
};

/**
 * Formats last-purchase info into the "Last order" display string — the
 * quantity and date of the most recent order (e.g. "3, Apr 3")
 *
 * @param info - The last purchase info for a variant pack, or undefined if none exists.
 * @param timezone - IANA timezone identifier used to interpret and render the date.
 * @returns A formatted display string, or an empty string when there is no data.
 */
export const formatLastOrderDisplay = (
  info: FulfillmentDateInput | undefined,
  timezone: string,
): string => {
  if (!info?.orderDate) {
    return '';
  }

  const dateMoment = moment.tz(info.orderDate, timezone);
  if (!dateMoment.isValid()) {
    return '';
  }

  return i18n.t('sales_portal.order_entry.table.last_order_quantity_date', {
    date: formatSummaryDateWithYear(dateMoment),
    quantity: info.quantity,
  });
};

/**
 * Transforms raw `getVariantPackLastPurchaseInfo` query items into a
 * `LastPurchaseInfoMap` keyed by variant pack UUID. Each entry carries the
 * raw fulfillment/order dates (used by the column's `sortingFn`) and a
 * pre-formatted `display` string rendered in the restaurant's timezone.
 *
 * Pre-formatting here (rather than per cell render) avoids per-row i18n
 * work on every render of the order entry table.
 *
 * Memoized on the input items reference and timezone — the calling loader
 * is responsible for keeping the items reference stable when the underlying
 * data hasn't changed (Apollo's `cache-first` policy handles that).
 *
 * @param items - Raw last-purchase-info items from the query, or undefined while loading.
 * @param timezone - IANA timezone identifier used to interpret and render dates.
 * @returns A lookup map keyed by variant pack UUID.
 */
export const useFulfilledDateTransformer = (
  items: ReadonlyArray<FulfilledDateTransformerItem> | undefined,
  timezone: string,
): LastPurchaseInfoMap => {
  return useMemo<LastPurchaseInfoMap>(() => {
    if (!items) {
      return {};
    }

    const map: LastPurchaseInfoMap = {};
    for (const item of items) {
      if (item.variant_pack_id) {
        const baseInfo = {
          fulfillmentDate: item.fulfillment_date ?? null,
          orderDate: item.order_date ?? null,
          quantity: item.quantity ?? 0,
        };
        map[item.variant_pack_id] = {
          ...baseInfo,
          display: formatFulfillmentDateDisplay(baseInfo, timezone),
        };
      }
    }
    return map;
  }, [items, timezone]);
};
